import { z } from 'zod';

import { AvailabilitySchema } from '@/shared/lib/availability';
import { MediaSchema } from '@/shared/lib/media';
import { MoneySchema } from '@/shared/lib/money';

// Storefront DTO. It is deliberately independent of the future OMS models:
// adapters map OMS responses into these shapes and fail closed on mismatch.

export const DirectionSchema = z.enum(['body', 'care', 'mind']);
export type Direction = z.infer<typeof DirectionSchema>;

export const ProductTypeSchema = z.enum(['top', 'bottom', 'practice', 'objects']);
export type ProductType = z.infer<typeof ProductTypeSchema>;

export const OccasionSchema = z.enum([
  'practice',
  'everyday',
  'walk',
  'home',
  'after-training',
  'gift',
]);
export type Occasion = z.infer<typeof OccasionSchema>;

const SlugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export { AvailabilitySchema, MediaSchema };
export type { Availability } from '@/shared/lib/availability';
export type { Media } from '@/shared/lib/media';

const HexSchema = z.string().regex(/^#[\da-f]{3,8}$/i);

export const ColorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  hex: HexSchema,
  /** Placeholder background for photos of this colour. */
  tint: HexSchema,
  /** Article shown in "О товаре" for this colour. */
  article: z.string().nullable(),
});
export type Color = z.infer<typeof ColorSchema>;

export const SpecSchema = z.object({ label: z.string(), value: z.string() });

export const BundleOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  short: z.string(),
  image: z.string().nullable(),
});
export const BundleSchema = z.object({
  fixedItems: z.array(z.object({ name: z.string(), image: z.string().nullable() })),
  choices: z.array(
    z.object({ id: z.string(), title: z.string(), options: z.array(BundleOptionSchema).min(1) }),
  ),
  gifts: z.array(z.object({ name: z.string(), image: z.string().nullable() })),
  giftNote: z.string(),
  heroImage: z.string().nullable(),
});
export type Bundle = z.infer<typeof BundleSchema>;

/** Catalog content of a variant (no commercial data). */
export const VariantContentSchema = z.object({
  sku: z.string().min(1).max(64),
  colorId: z.string().nullable(),
  size: z.string().nullable(),
  /** Choice id → option id, only for bundles. */
  bundleOptions: z.record(z.string(), z.string()).nullable(),
});

export const VariantSchema = VariantContentSchema.extend({
  /** Missing price means "not for sale", never zero. */
  price: MoneySchema.nullable(),
  compareAtPrice: MoneySchema.nullable(),
  availability: AvailabilitySchema,
  /** Human-readable expected shipment for preorder variants. */
  shipDate: z.string().nullable(),
});
export type Variant = z.infer<typeof VariantSchema>;

export const ProductContentSchema = z.object({
  id: z.string().min(1),
  slug: SlugSchema,
  name: z.string().min(1),
  kind: z.enum(['item', 'bundle']),
  direction: DirectionSchema,
  type: ProductTypeSchema,
  occasions: z.array(OccasionSchema),
  categoryLabel: z.string(),
  description: z.string(),
  /** Shown in the catalog listing; unlisted products are reachable from sets, looks and hotspots. */
  listed: z.boolean(),
  /** Merchandising label on cards ("Предзаказ", "В наличии", "Скоро"). */
  badge: z.string().nullable(),
  /** Short placeholder label and tint for product tiles without a photo ("Топ", "Коврик"). */
  shortLabel: z.string(),
  tint: HexSchema,
  media: z.array(MediaSchema),
  /** PDP gallery; empty means the design placeholders (5 photos + video). */
  gallery: z.array(MediaSchema),
  colors: z.array(ColorSchema),
  sizes: z.array(z.string()),
  /** Label used when a product has no size grid ("Единый размер", "Один размер"). */
  oneSizeLabel: z.string().nullable(),
  specs: z.array(SpecSchema),
  lookSlugs: z.array(SlugSchema),
  complementSlugs: z.array(SlugSchema),
  sizeAdvice: z.boolean(),
  bundle: BundleSchema.nullable(),
  variants: z.array(VariantContentSchema).min(1),
});
export type ProductContent = z.infer<typeof ProductContentSchema>;

export const ProductSchema = ProductContentSchema.extend({
  variants: z.array(VariantSchema).min(1),
  /** Lowest variant price; null when nothing is priced. */
  price: MoneySchema.nullable(),
  compareAtPrice: MoneySchema.nullable(),
  availability: AvailabilitySchema,
  /** Loyalty points for the lowest price, computed by the pricing side. */
  bonusPoints: z.number().int().min(0).nullable(),
  installment: z.object({ count: z.number().int().min(2), amount: MoneySchema }).nullable(),
});
export type Product = z.infer<typeof ProductSchema>;
export const ProductListSchema = z.array(ProductSchema);

export const CatalogSortSchema = z.enum(['default', 'price-asc', 'price-desc']);
export type CatalogSort = z.infer<typeof CatalogSortSchema>;

export const CatalogQuerySchema = z.object({
  direction: DirectionSchema.nullable(),
  occasions: z.array(OccasionSchema),
  types: z.array(ProductTypeSchema),
  size: z.string().max(8).nullable(),
  color: z.string().max(32).nullable(),
  availability: z.enum(['in_stock', 'preorder']).nullable(),
  sort: CatalogSortSchema,
});
export type CatalogQuery = z.infer<typeof CatalogQuerySchema>;
